'use client';

import React, { useMemo, useState } from 'react';
import GfSectionMark from '@/components/common/GfSectionMark';
import TourCard, { TOUR_CATEGORY_LABELS, TOUR_CATEGORY_ICONS, DEFAULT_TOUR_ICON } from '@/components/tours/TourCard';
import { t } from '@/i18n/translations';

/**
 * Секция «Экскурсии и туры» на странице города. Данные приходят из Sanity
 * (`cityPage.tours`, см. src/sanity/queries/city.js): документы типа `tour`,
 * привязанные к городу. Аналог CityHotels — карточки без фото.
 *
 * Фильтры — клиентские, по уже загруженному списку (до 24 туров): категория
 * (одна за раз) + тип тура / «с детьми» / язык сайта.
 * Показываем первые 6; остальные — под кнопкой «Показать ещё».
 */

const PREVIEW_COUNT = 6;
const GROUP_TOUR_TYPES = new Set(['group', 'small_group']);

const isGroupTour = (tour) => GROUP_TOUR_TYPES.has(tour?.groupType);
const isPrivateTour = (tour) => tour?.groupType === 'private';

export default function CityTours({
    tours,
    lang = 'ru',
    countrySlug,
    citySlug,
    cityDisplayName,
}) {
    const isRu = lang === 'ru';
    const ui = t(lang, 'toursWidgetsUi').cityTours;
    const list = (tours || []).filter(Boolean);
    const [expanded, setExpanded] = useState(false);
    const [cat, setCat] = useState(null);
    const [groupKind, setGroupKind] = useState(null);
    const [kidsOnly, setKidsOnly] = useState(false);
    const [langOnly, setLangOnly] = useState(false);

    // Категории, реально встречающиеся в списке, с количеством — для чипов
    const catCounts = useMemo(() => {
        const counts = new Map();
        for (const t of list) {
            for (const c of t.categories || []) {
                if (TOUR_CATEGORY_LABELS[c]) counts.set(c, (counts.get(c) || 0) + 1);
            }
        }
        // Стабильный порядок — как в TOUR_CATEGORY_LABELS
        return Object.keys(TOUR_CATEGORY_LABELS)
            .filter((c) => counts.has(c))
            .map((c) => [c, counts.get(c)]);
    }, [list]);

    const groupCounts = useMemo(() => {
        const counts = { group: 0, private: 0 };
        for (const t of list) {
            if (isPrivateTour(t)) counts.private += 1;
            else if (isGroupTour(t)) counts.group += 1;
        }
        return counts;
    }, [list]);

    const hasGroupTypeFilter = groupCounts.group > 0 && groupCounts.private > 0;
    const hasKids = list.some((t) => t.childFriendly);
    // Переключатель языка показываем, только если он что-то меняет
    const inLang = (t) => Array.isArray(t.languages) && t.languages.includes(lang);
    const langMatches = list.filter(inLang).length;
    const hasLangToggle = langMatches > 0 && langMatches < list.length;

    const filtered = list.filter((t) =>
        (!cat || (t.categories || []).includes(cat))
        && (!groupKind || (groupKind === 'group' ? isGroupTour(t) : isPrivateTour(t)))
        && (!kidsOnly || t.childFriendly)
        && (!langOnly || inLang(t))
    );

    const hasFilter = Boolean(cat) || Boolean(groupKind) || kidsOnly || langOnly;
    const resetFilters = () => { setCat(null); setGroupKind(null); setKidsOnly(false); setLangOnly(false); };

    if (list.length === 0) return null;

    const visible = expanded ? filtered : filtered.slice(0, PREVIEW_COUNT);
    const hasMore = filtered.length > PREVIEW_COUNT;

    const allHref = countrySlug ? `/${lang}/${countrySlug}/tours` : null;

    return (
        <section className="gf-sec" id="tours" style={{ paddingTop: 0 }}>
            <GfSectionMark
                eyebrow={ui.eyebrow(cityDisplayName)}
                title={ui.title}
                tag={String(list.length)}
                allHref={allHref}
                allLabel={ui.allLabel}
            />
            <p className="gf-lead">{ui.lead}</p>

            {/* Фильтры: категории + переключатели. Чипы — по загруженному списку. */}
            {(catCounts.length > 1 || hasGroupTypeFilter || hasKids || hasLangToggle) && (
                <div className="to-filters">
                    {catCounts.length > 1 && (
                        <>
                            <button
                                type="button"
                                className={`tof${cat ? '' : ' active'}`}
                                onClick={() => { setCat(null); setExpanded(false); }}
                            >
                                {ui.all} <em>{list.length}</em>
                            </button>
                            {catCounts.map(([c, n]) => {
                                const Icon = TOUR_CATEGORY_ICONS[c] || DEFAULT_TOUR_ICON;
                                return (
                                    <button
                                        type="button"
                                        key={c}
                                        className={`tof${cat === c ? ' active' : ''}`}
                                        onClick={() => { setCat(cat === c ? null : c); setExpanded(false); }}
                                    >
                                        <Icon aria-hidden /> {TOUR_CATEGORY_LABELS[c][isRu ? 0 : 1]} <em>{n}</em>
                                    </button>
                                );
                            })}
                        </>
                    )}
                    {(catCounts.length > 1 && (hasGroupTypeFilter || hasKids || hasLangToggle)) && <span className="tof-div" aria-hidden />}
                    {hasGroupTypeFilter && (
                        <>
                            <button type="button" className={`tof${groupKind === 'group' ? ' active' : ''}`} onClick={() => { setGroupKind(groupKind === 'group' ? null : 'group'); setExpanded(false); }}>
                                {ui.group} <em>{groupCounts.group}</em>
                            </button>
                            <button type="button" className={`tof${groupKind === 'private' ? ' active' : ''}`} onClick={() => { setGroupKind(groupKind === 'private' ? null : 'private'); setExpanded(false); }}>
                                {ui.private} <em>{groupCounts.private}</em>
                            </button>
                        </>
                    )}
                    {hasKids && (
                        <button type="button" className={`tof${kidsOnly ? ' active' : ''}`} onClick={() => { setKidsOnly(!kidsOnly); setExpanded(false); }}>
                            {ui.kidFriendly}
                        </button>
                    )}
                    {hasLangToggle && (
                        <button type="button" className={`tof${langOnly ? ' active' : ''}`} onClick={() => { setLangOnly(!langOnly); setExpanded(false); }}>
                            {ui.inLang}
                        </button>
                    )}
                    {hasFilter && (
                        <button type="button" className="tof-reset" onClick={resetFilters}>
                            {ui.reset}
                        </button>
                    )}
                </div>
            )}

            {filtered.length === 0 ? (
                <div className="to-empty">
                    <p>{ui.emptyTitle}</p>
                    <button type="button" className="gf-more" onClick={resetFilters}>
                        {ui.resetFilters}
                    </button>
                </div>
            ) : (
                <div className={`ct-tours${visible.length === 2 ? ' two' : ''}`}>
                    {visible.map((t) => (
                        <TourCard key={t._id} tour={t} lang={lang} countrySlug={countrySlug} citySlug={citySlug} />
                    ))}
                </div>
            )}

            {hasMore && !expanded && (
                <div className="gf-more-wrap">
                    <button type="button" className="gf-more" onClick={() => setExpanded(true)}>
                        {ui.showMore(filtered.length - PREVIEW_COUNT)}
                    </button>
                </div>
            )}
        </section>
    );
}
